import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Key, Lock, Eye, EyeOff, Check, X, 
  AlertCircle, CheckCircle2, Loader2, Shield 
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useToast } from '@/lib/toast';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';

interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
}

export function ChangePasswordModal({ open, onClose }: ChangePasswordModalProps) {
  const navigate = useNavigate();
  const { session, signOut } = useAuth();
  const toast = useToast();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) {
        onClose();
      }
    };
    if (open) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, busy, onClose]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowCurrentPassword(false);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
      setError(null);
      setSuccess(false);
      setBusy(false);
    }
  }, [open]);

  if (!open) return null;

  // Criteria checks
  const hasMinLength = newPassword.length >= 12;
  const hasUpper = /[A-Z]/.test(newPassword);
  const hasLower = /[a-z]/.test(newPassword);
  const hasNumber = /[0-9]/.test(newPassword);
  const hasSpecial = /[^A-Za-z0-9]/.test(newPassword);
  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;
  const isDifferentFromCurrent = !currentPassword || newPassword !== currentPassword;

  // Calculate score (0 to 4)
  const score = [
    hasMinLength,
    hasUpper && hasLower,
    hasNumber,
    hasSpecial
  ].filter(Boolean).length;

  const getStrengthLabel = () => {
    if (!newPassword) return { label: '', color: 'bg-muted' };
    if (score <= 1) return { label: 'Weak', color: 'bg-red-500' };
    if (score === 2) return { label: 'Fair', color: 'bg-amber-500' };
    if (score === 3) return { label: 'Good', color: 'bg-blue-500' };
    return { label: 'Strong', color: 'bg-emerald-500' };
  };

  const strength = getStrengthLabel();
  const allCriteriaMet = hasMinLength && hasUpper && hasLower && hasNumber && hasSpecial && passwordsMatch && isDifferentFromCurrent;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase) {
      setError('Authentication service is not connected.');
      return;
    }

    if (!currentPassword) {
      setError('Current password is required.');
      return;
    }

    if (!newPassword) {
      setError('New password is required.');
      return;
    }

    if (!confirmPassword) {
      setError('Confirm password is required.');
      return;
    }

    if (!hasMinLength || !hasUpper || !hasLower || !hasNumber || !hasSpecial) {
      setError('Password does not meet the required security requirements.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New password and confirmation password do not match.');
      return;
    }

    if (currentPassword === newPassword) {
      setError('New password must be different from current password.');
      return;
    }

    setBusy(true);
    setError(null);

    try {
      // 1. Obtain the freshest access token available
      let token = session?.access_token;
      let userEmail = session?.user?.email;

      try {
        const { data: refreshData } = await supabase.auth.refreshSession();
        if (refreshData?.session?.access_token) {
          token = refreshData.session.access_token;
          if (refreshData.session.user?.email) {
            userEmail = refreshData.session.user.email;
          }
        }
      } catch (refreshErr) {
        console.warn('Silent session refresh attempt:', refreshErr);
      }

      if (!token) {
        const sessionRes = await supabase.auth.getSession();
        token = sessionRes?.data?.session?.access_token || session?.access_token;
        if (!userEmail) {
          userEmail = sessionRes?.data?.session?.user?.email;
        }
      }

      const response = await fetch('/api/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          email: userEmail,
          currentPassword,
          newPassword,
          isForced: false
        })
      });

      const resData = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(resData.error || resData.message || 'Unable to update your password. Please try again.');
      }

      setSuccess(true);
      const successMessage = 'Password changed successfully. You have been signed out. Please log in again with your new password.';
      toast.push(successMessage);

      // Invalidate current session and clear auth state
      await signOut();

      // Close modal and redirect to login page replacing history to prevent browser Back button access
      onClose();
      navigate('/login?reason=password_changed', { replace: true });
      window.history.replaceState(null, '', '/login?reason=password_changed');

    } catch (err: any) {
      console.error('Password change error:', err);
      setError(err?.message || 'Unable to change password. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div 
        className="w-full max-w-md rounded-[2rem] border border-[rgba(255,255,255,0.08)] bg-card shadow-2xl overflow-hidden transition-all neo"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border/50 px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary neo-interactive">
              <Key className="h-5 w-5" />
            </div>
            <div>
              <h2 className="font-display text-lg font-bold text-foreground">Change Password</h2>
              <p className="text-xs text-muted-foreground">Update your account credentials</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-xl p-2 text-muted-foreground hover:text-foreground hover:bg-black/5 dark:hover:bg-white/5 transition-colors disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {success ? (
            <div className="py-8 text-center space-y-3">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-500">
                <CheckCircle2 className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-bold text-foreground">Password Updated!</h3>
              <p className="text-sm text-muted-foreground">Your password has been changed successfully.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="flex items-start gap-2.5 rounded-xl bg-destructive/10 border border-destructive/20 p-3.5 text-xs text-destructive">
                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              {/* Current Password */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Current Password *
                </label>
                <div className="relative">
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground">
                    <Lock className="h-4 w-4" />
                  </div>
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    required
                    disabled={busy}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                    className="w-full rounded-xl border border-input bg-background/50 pl-10 pr-10 py-2.5 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20 neo-inset disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1 disabled:opacity-50"
                    tabIndex={-1}
                  >
                    {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  New Password *
                </label>
                <div className="relative">
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground">
                    <Key className="h-4 w-4" />
                  </div>
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    required
                    disabled={busy}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min. 12 chars)"
                    className="w-full rounded-xl border border-input bg-background/50 pl-10 pr-10 py-2.5 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20 neo-inset disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1 disabled:opacity-50"
                    tabIndex={-1}
                  >
                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>

                {/* Password Strength Indicator */}
                {newPassword && (
                  <div className="pt-2 space-y-2">
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="text-muted-foreground">Strength</span>
                      <span className={cn(
                        "font-semibold",
                        score <= 1 ? "text-red-500" :
                        score === 2 ? "text-amber-500" :
                        score === 3 ? "text-blue-500" : "text-emerald-500"
                      )}>
                        {strength.label}
                      </span>
                    </div>
                    <div className="grid grid-cols-4 gap-1.5 h-1.5">
                      {[1, 2, 3, 4].map((level) => (
                        <div
                          key={level}
                          className={cn(
                            "h-full rounded-full transition-all duration-300",
                            score >= level ? strength.color : "bg-muted"
                          )}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Confirm Password */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Confirm New Password *
                </label>
                <div className="relative">
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground">
                    <Shield className="h-4 w-4" />
                  </div>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    disabled={busy}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repeat new password"
                    className={cn(
                      "w-full rounded-xl border bg-background/50 pl-10 pr-10 py-2.5 text-sm outline-none transition neo-inset disabled:opacity-50 disabled:cursor-not-allowed",
                      confirmPassword && !passwordsMatch ? "border-red-500 focus:ring-2 focus:ring-red-500/20" : "border-input focus:border-primary focus:ring-2 focus:ring-primary/20"
                    )}
                  />
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1 disabled:opacity-50"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {confirmPassword && !passwordsMatch && (
                  <p className="text-[11px] text-red-500 mt-1">New password and confirmation password do not match.</p>
                )}
              </div>

              {/* Requirements Checklist */}
              <div className="rounded-xl bg-muted/40 p-3 space-y-1.5 text-xs text-muted-foreground border border-border/40">
                <p className="font-semibold text-foreground text-[11px] uppercase tracking-wider mb-2">Password Requirements</p>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[11px]">
                  <div className="flex items-center gap-1.5">
                    {hasMinLength ? <Check className="h-3.5 w-3.5 text-emerald-500 shrink-0" /> : <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50 mx-1 shrink-0" />}
                    <span className={hasMinLength ? "text-foreground font-medium" : ""}>At least 12 characters</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {hasUpper && hasLower ? <Check className="h-3.5 w-3.5 text-emerald-500 shrink-0" /> : <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50 mx-1 shrink-0" />}
                    <span className={hasUpper && hasLower ? "text-foreground font-medium" : ""}>Upper & lower case</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {hasNumber ? <Check className="h-3.5 w-3.5 text-emerald-500 shrink-0" /> : <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50 mx-1 shrink-0" />}
                    <span className={hasNumber ? "text-foreground font-medium" : ""}>At least one number</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {hasSpecial ? <Check className="h-3.5 w-3.5 text-emerald-500 shrink-0" /> : <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50 mx-1 shrink-0" />}
                    <span className={hasSpecial ? "text-foreground font-medium" : ""}>Special symbol (!@#$)</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={busy}
                  className="rounded-xl px-4 py-2.5 text-xs font-semibold text-muted-foreground hover:text-foreground hover:bg-black/5 dark:hover:bg-white/5 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={busy || !currentPassword || !allCriteriaMet}
                  className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-xs font-bold text-primary-foreground shadow hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed neo-interactive"
                >
                  {busy ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Updating Password...
                    </>
                  ) : (
                    <>
                      <Check className="h-3.5 w-3.5" />
                      Update Password
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
